import React, {
  useState, useEffect, useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { matchSorter } from 'match-sorter';
import { useTheme } from 'emotion-theming';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import Button from '../../../shared/_TBComponents/Button';
import Icon from '../../../shared/_TBComponents/Icon';
import Text from '../../../shared/_TBComponents/Text';
import hexToRgba from '../../../shared/utils/hexToRgba';
import { showErrorToast } from '../../../shared/utils/toastManager';
import EmptyStateArrow from './presentational/EmptyStateArrow';
import NavigatorPreviewPanel from './presentational/NavigatorPreviewPanel';
import NavigatorSelectorPanel from './presentational/NavigatorSelectorPanel';
import AddImportProgramModal from './presentational/AddImportProgramModal';
import ProgramTemplatesModal from './presentational/ProgramTemplatesModal';
import EditProgramModal from './presentational/EditProgramModal';
import ShareProgramModal from './presentational/ShareProgramModal';
import SharePlaneIcon from './presentational/SharePlaneIcon';
import ConfirmModal from './presentational/modals/ConfirmModal';
import LoadToCalendarModal from './presentational/modals/LoadToCalendarModal';
import {
  useActionMenu,
  ActionMenuWrapper,
  ActionMenuButton,
  ActionMenuContainer,
  ActionMenuDivider,
  ActionMenuItemButton,
} from './presentational/tables/NavigatorManageTable';
import { fetchProgramPreview, fetchPrograms } from '../ducks/navigatorActions';
import {
  setSelectedReduxNonDateBasedProgram,
  setProgramType,
  deleteSavedWorkout,
} from '../../calendar/ducks/calendarActions';
import {
  fetchPendingImports,
  abandonImport,
  loadImportForReview,
  clearLastImportedProgramId,
} from '../../ai-program-import/ducks';

const Container = styled.div`
  display: flex;
  height: 100%;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  border-right: none;
  padding: 16px;
  gap: 8px;
  overflow-y: auto;
  box-shadow: inset 8px 0 12px -12px rgba(0,0,0,0.18);
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  max-width: 700px;
  margin-bottom: ${(props) => (props.isMobile ? '12px' : '0')};
`;

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
`;

const HeaderActionMenuButton = styled(ActionMenuButton)`
  height: 100%;
  aspect-ratio: 1;
  margin-right: 8px;
`;

const FlipIconWrap = styled.span`
  display: inline-flex;
  transform: scaleY(-1);
`;

const ProgramItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  margin-bottom: 8px;
  gap: 8px;
  border: ${(props) => (props.isSelected
    ? `2px solid ${props.bgColor}`
    : '1px solid #EEE')};
  background: ${(props) => (props.isSelected ? props.selectedBg : 'transparent')};
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;

  &:hover {
    opacity: 0.7;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  height: 100%;
  max-width: 385px;
  padding-top: 8px;
`;

const SelectEmptyState = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  height: 100%;
  max-width: 400px;
  padding-top: 60px;
`;

const EmptyStateText = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 8px;
`;

const StickySectionHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
  font-family: 'Nunito Sans';
  font-size: 11px;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 8px 0 6px 0;
`;

// Pending imports read as plain rows in the sidebar — same visual language
// as ProgramItem so the eye can scan the column without context-switching
// to "yellow = warning". The yellow live in the banner on the right side
// once the row is selected, which is enough signal that it's unfinished.
const PendingItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  margin-bottom: 8px;
  gap: 8px;
  border: ${(props) => (props.isSelected
    ? `2px solid ${props.bgColor}`
    : '1px solid #EEE')};
  background: ${(props) => (props.isSelected ? props.selectedBg : 'transparent')};
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;

  &:hover {
    opacity: 0.7;
  }
`;

const PendingDetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 406px;
`;

// flex:1 + min-height so the card claims the same vertical space as the
// populated program detail — otherwise it collapses to its content height
// and reads as floating mid-page.
const PendingDetailCard = styled.div`
  background: #FFFFFF;
  border: 1px solid #EEEEEE;
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  flex: 1;
  min-height: 600px;
  box-sizing: border-box;
`;

// Only renders when the LD flag `aiImportHomeLanding` is ON — that path
// stashes lastImportedProgramId. Width capped to match the HeaderContainer
// so it never extends past the title row.
const ImportSuccessBanner = styled.div`
  background: rgba(13, 204, 138, 0.12);
  border: 1px solid #0DCC8A;
  border-radius: 4px;
  padding: 8px 16px;
  margin-bottom: 12px;
  max-width: 700px;
  width: 100%;
  box-sizing: border-box;
`;

const ImportSuccessBannerBody = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #0E7F5A;
  margin: 0;
  line-height: 1.5;
`;

const PendingBanner = styled.div`
  background: rgba(255, 203, 71, 0.2);
  border: 1px solid #FFCB47;
  border-radius: 4px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
`;

const PendingBannerBody = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #444444;
  margin: 0;
  line-height: 1.5;
  width: 100%;
`;

const PendingButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PendingDeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 2px solid #EEEEEE;
  border-radius: 4px;
  padding: 6px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #444444;
  letter-spacing: 0.39px;
  line-height: 1.5;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease;

  &:hover:not(:disabled) {
    border-color: #C9C9D4;
    background: #FAFAFB;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const PendingReviewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #444444;
  border: none;
  border-radius: 4px;
  padding: 6px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #FFFFFF;
  letter-spacing: 0.39px;
  line-height: 1.5;
  cursor: pointer;
  transition: background 160ms ease, transform 160ms ease, box-shadow 160ms ease;

  &:hover:not(:disabled) {
    background: #2a2a2a;
    box-shadow: 0 4px 16px rgba(48, 49, 51, 0.25);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const MobilePage = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const MobileHeader = styled.div`
  padding: 16px 16px 0 16px;
  flex-shrink: 0;
`;

const MobileBackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #77A7FF;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MobileSectionTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1A1A1A;
  padding: 6px 0 4px;
`;

const MobileDetailContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 16px;
  min-height: 0;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: #999; }
  scrollbar-width: thin;
  scrollbar-color: #ccc #f1f1f1;
`;

const ProgramNavigator = ({
  accountCode, programId, isMobile,
}) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountPrimaryColor = theme?.colorPrimary || currentUser?.userSkin?.primary || '#77A7FF';
  const selectedProgramBg = hexToRgba(accountPrimaryColor, 0.05);

  const programs = useSelector((state) => state.navigator.data.programs);
  const programsError = useSelector((state) => state.navigator.data.programsError);
  const isLoading = useSelector((state) => state.navigator.ui.isLoadingPrograms);
  const pendingImports = useSelector((state) => state.aiProgramImport.pendingImports || []);
  const isAbandoning = useSelector((state) => state.aiProgramImport.isAbandoning);
  const isLoadingHistoryDetail = useSelector(
    (state) => state.aiProgramImport.isLoadingHistoryDetail,
  );
  // Stays null unless the LD flag `aiImportHomeLanding` is ON and the
  // wizard just landed the coach here after a confirm.
  const lastImportedProgramId = useSelector(
    (state) => state.aiProgramImport.lastImportedProgramId,
  );

  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedPendingId, setSelectedPendingId] = useState(null);
  const [calPickerOpen, setCalPickerOpen] = useState(false);
  // Confirm modal for the trash button. Holds the import id we're about
  // to delete so it survives the modal mount even if the user clicks
  // through to a different pending import after opening the dialog.
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
  const [programToEdit, setProgramToEdit] = useState(null);
  const [programToShare, setProgramToShare] = useState(null);
  const [programToDelete, setProgramToDelete] = useState(null);
  const [isDeletingProgram, setIsDeletingProgram] = useState(false);
  const {
    isOpen: isActionMenuOpen, menuStyle, menuRef, buttonRef, openMenu, close: closeActionMenu,
  } = useActionMenu();

  const addProgramOptions = [
    {
      key: 'build',
      icon: 'build',
      title: 'Build your own',
      description: 'Start a new program from scratch in Build',
      onClick: () => history.push('/program/create', { from: location.pathname }),
    },
    {
      key: 'templates',
      icon: 'templates',
      title: 'Browse our templates',
      description: 'Import a pre-built program from our template library.',
      onClick: () => setIsTemplatesOpen(true),
    },
    {
      key: 'ai',
      icon: 'sparkle',
      title: 'AI Import',
      description: 'Import your program from an existing file.',
      onClick: () => history.push('/import/program'),
    },
  ];

  const isInvalidProgramId = !!programId
    && !isLoading
    && programs?.length > 0
    && !programs.some((p) => String(p.id) === String(programId));

  // Find program by programId from URL
  useEffect(() => {
    if (programId && programs.length > 0) {
      const found = programs.find((p) => String(p.id) === String(programId));
      setSelectedProgram(found || null);
    } else if (!programId) {
      setSelectedProgram(null);
    }
  }, [programId, programs]);

  useEffect(() => {
    if (selectedProgram && accountCode) {
      dispatch(fetchProgramPreview(accountCode, selectedProgram.id));
    }
  }, [selectedProgram?.id]);

  useEffect(() => {
    if (accountCode) {
      dispatch(fetchPendingImports());
    }
  }, [accountCode]);

  // Selecting a saved program clears any pending selection so the panels
  // don't fight over the main content area.
  useEffect(() => {
    if (selectedProgram) setSelectedPendingId(null);
  }, [selectedProgram?.id]);

  const selectedPendingImport = pendingImports.find(
    (p) => String(p.id) === String(selectedPendingId),
  ) || null;

  const handleSelectPending = useCallback((historyId) => {
    setSelectedPendingId(historyId);
    if (programId) {
      // Drop the program param from the URL so the saved-program detail
      // doesn't re-render under the pending detail.
      history.push('/dashboard/programs');
    }
  }, [programId, history]);

  const handleReviewPending = useCallback(async () => {
    if (!selectedPendingId) return;
    const result = await dispatch(loadImportForReview(selectedPendingId));
    if (result?.success) {
      history.push('/import/program');
    } else {
      showErrorToast(result?.error || 'Unable to open the import for review');
    }
  }, [dispatch, history, selectedPendingId]);

  // Abandon is destructive and not undoable on the backend, so the trash
  // button opens a confirm dialog instead of abandoning immediately — the
  // extra click protects against fat-fingering on the navigator pane.
  const handleAbandonPending = useCallback(() => {
    if (!selectedPendingId) return;
    setPendingDeleteId(selectedPendingId);
  }, [selectedPendingId]);

  const handleCancelDelete = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDeleteId) return;
    const result = await dispatch(abandonImport(pendingDeleteId));
    if (result?.success) {
      // Clear both the modal id and the selection so the navigator falls
      // back to its empty / programs view after the row disappears.
      if (String(pendingDeleteId) === String(selectedPendingId)) {
        setSelectedPendingId(null);
      }
      setPendingDeleteId(null);
    } else {
      showErrorToast(result?.error || 'Unable to delete the pending import');
    }
  }, [dispatch, pendingDeleteId, selectedPendingId]);

  // Auto-dismiss the post-import success banner. Two triggers:
  //   1. The selected program no longer matches the just-imported id (the
  //      coach navigated away — the banner shouldn't follow them).
  //   2. A 6s timer so the banner doesn't linger if they sit on the page.
  // The state is non-persisted so a refresh also clears it naturally. No-
  // op when lastImportedProgramId is null (LD flag OFF or no recent
  // import).
  useEffect(() => {
    if (!lastImportedProgramId) return undefined;
    if (selectedProgram && String(selectedProgram.id) !== String(lastImportedProgramId)) {
      dispatch(clearLastImportedProgramId());
      return undefined;
    }
    const timer = setTimeout(() => {
      dispatch(clearLastImportedProgramId());
    }, 6000);
    return () => clearTimeout(timer);
  }, [lastImportedProgramId, selectedProgram, dispatch]);

  const handleBuildProgram = useCallback(() => {
    if (!selectedProgram) return;
    dispatch(setProgramType('free-form-program'));
    dispatch(setSelectedReduxNonDateBasedProgram(selectedProgram));
    history.push('/build');
  }, [selectedProgram, dispatch, history]);

  const confirmDeleteProgram = () => {
    if (!programToDelete) return;
    setIsDeletingProgram(true);
    dispatch(deleteSavedWorkout(accountCode, programToDelete.id, {
      onSuccess: () => {
        setIsDeletingProgram(false);
        setProgramToDelete(null);
        dispatch(fetchPrograms(accountCode));
        history.push('/dashboard/programs');
      },
      onError: () => setIsDeletingProgram(false),
    }));
  };

  const selectorButtons = [
    {
      name: 'Add',
      icon: 'add-program',
      iconSize: 20,
      iconColor: '#1A1A1A',
      href: '/program/create',
      onClick: (e) => {
        const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
        if (!isModified) {
          e.preventDefault();
          setIsAddOpen(true);
        }
      },
    },
    {
      name: 'Manage',
      href: '/dashboard/manage-programs',
      onClick: (e) => {
        const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
        if (!isModified) {
          e.preventDefault();
          history.push('/dashboard/manage-programs');
        }
      },
    },
  ];

  const renderProgramList = ({ selectedRef, searchFilter }) => {
    const filtered = searchFilter && programs
      ? matchSorter(programs, searchFilter, { keys: ['programName'] })
      : programs;

    // Pending imports are filtered with the same search term so the group
    // collapses naturally when the coach is hunting for a specific program.
    // When there's no active search we render the list alphabetically by
    // display name; with a search active we defer to matchSorter's
    // relevance ordering so the closest match floats to the top.
    const pendingDisplayName = (it) => (
      it.programName || it.originalProgramName || 'Untitled import'
    );
    const filteredPending = searchFilter
      ? matchSorter(pendingImports, searchFilter, { keys: ['programName', 'originalProgramName'] })
      : [...pendingImports].sort((a, b) => pendingDisplayName(a).localeCompare(
        pendingDisplayName(b),
        undefined,
        { sensitivity: 'base' },
      ));
    const hasPending = filteredPending.length > 0;

    if (programsError && !searchFilter && !hasPending) {
      return (
        <Text styledTextProps={{ fontSize: '14px', color: '#666' }}>
          {programsError}
        </Text>
      );
    }

    const programsContent = !filtered || filtered.length === 0
      ? (
        <Text styledTextProps={{ fontSize: '14px', color: '#666' }}>
          {searchFilter ? 'No programs match your search' : 'No programs found'}
        </Text>
      ) : filtered.map((program) => (
        <ProgramItem
          key={program.id}
          isSelected={selectedProgram?.id === program.id}
          bgColor={accountPrimaryColor}
          selectedBg={selectedProgramBg}
          ref={selectedProgram?.id === program.id ? selectedRef : null}
          onClick={() => history.push(`/dashboard/programs/${program.id}`)}
        >
          <Text
            styledTextProps={{
              fontSize: '16px',
              fontWeight: 700,
              color: '#1A1A1A',
            }}
          >
            {program.programName}
          </Text>
        </ProgramItem>
      ));

    return (
      <>
        {hasPending && (
          <>
            <StickySectionHeader data-sticky-header>Unfinished Imports</StickySectionHeader>
            {filteredPending.map((item) => {
              const name = item.programName || item.originalProgramName || 'Untitled import';
              const isSelected = String(item.id) === String(selectedPendingId);
              return (
                <PendingItem
                  key={item.id}
                  isSelected={isSelected}
                  bgColor={accountPrimaryColor}
                  selectedBg={selectedProgramBg}
                  onClick={() => handleSelectPending(item.id)}
                >
                  <Text styledTextProps={{ fontSize: '16px', fontWeight: isSelected ? 700 : 500 }}>
                    {name}
                  </Text>
                </PendingItem>
              );
            })}
            <StickySectionHeader data-sticky-header>Programs</StickySectionHeader>
          </>
        )}
        {programsContent}
      </>
    );
  };

  let mainContent;
  if (selectedPendingImport) {
    const pendingName = selectedPendingImport.programName
      || selectedPendingImport.originalProgramName
      || 'Untitled import';
    mainContent = (
      <>
        <HeaderContainer isMobile={isMobile}>
          <InfoWrapper>
            <Text styledTextProps={{ fontSize: '24px', fontWeight: 700, color: '#1A1A1A' }}>
              {pendingName}
            </Text>
          </InfoWrapper>
        </HeaderContainer>
        <PendingDetailCard>
          <PendingDetailWrapper>
            <PendingBanner>
              <PendingBannerBody>
                This is an unfinished import. The next step is to review and confirm
                the exercises in the import.
              </PendingBannerBody>
            </PendingBanner>
            <PendingButtonRow>
              <PendingDeleteButton
                type='button'
                onClick={handleAbandonPending}
                disabled={isAbandoning}
              >
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
                  <path
                    d='M2 4 H14 M5.33 4 V2.67 a1 1 0 0 1 1-1 h3.33 a1 1 0 0 1 1 1 V4 M3.33 4 L4 13.33 a1 1 0 0 0 1 1 h6 a1 1 0 0 0 1-1 L12.67 4 M6.67 7.33 V12 M9.33 7.33 V12'
                    stroke='#FF0505'
                    strokeWidth='1.33'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                Delete
              </PendingDeleteButton>
              <PendingReviewButton
                type='button'
                onClick={handleReviewPending}
                disabled={isLoadingHistoryDetail}
              >
                Review
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
                  <path
                    d='M3 8 H13 M9 4 L13 8 L9 12'
                    stroke='#FFFFFF'
                    strokeWidth='1.67'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </PendingReviewButton>
            </PendingButtonRow>
          </PendingDetailWrapper>
        </PendingDetailCard>
      </>
    );
  } else if (selectedProgram) {
    const showImportSuccess = lastImportedProgramId
      && String(lastImportedProgramId) === String(selectedProgram.id);
    mainContent = (
      <>
        {showImportSuccess && (
          <ImportSuccessBanner>
            <ImportSuccessBannerBody>
              Your file was imported successfully. Here is your new program!
            </ImportSuccessBannerBody>
          </ImportSuccessBanner>
        )}
        <HeaderContainer isMobile={isMobile}>
          <InfoWrapper>
            <Text styledTextProps={{ fontSize: '24px', fontWeight: 700, color: '#1A1A1A' }}>
              {selectedProgram.programName}
            </Text>
          </InfoWrapper>
          <HeaderActions>
            <Button
              name='Build'
              icon='build'
              iconColor='#fff'
              customBorderRadius='4px'
              noBorder
              bgColor='#1A1A1A'
              txtColor='#fff'
              fontSize='16px'
              onClick={handleBuildProgram}
              styledWrapperProps={{
                boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)',
                padding: '6px 16px',
              }}
              styledTextProps={{ fontWeight: '700', letterSpacing: '0.5px' }}
            />
            <ActionMenuWrapper>
              <HeaderActionMenuButton ref={buttonRef} type='button' onClick={openMenu} aria-label='Program actions'>
                <Icon icon='dots' size={20} color='currentColor' styledProps={{ display: 'flex' }} />
              </HeaderActionMenuButton>
              {isActionMenuOpen && createPortal(
                <ActionMenuContainer ref={menuRef} style={menuStyle}>
                  <ActionMenuItemButton
                    type='button'
                    onClick={() => { setProgramToEdit(selectedProgram); closeActionMenu(); }}
                  >
                    <Icon icon='edit-workout' size={16} color='#1A1A1A' />
                    Edit
                  </ActionMenuItemButton>
                  <ActionMenuItemButton
                    type='button'
                    onClick={() => { setCalPickerOpen(true); closeActionMenu(); }}
                  >
                    <FlipIconWrap>
                      <Icon icon='download' size={16} color='#1A1A1A' />
                    </FlipIconWrap>
                    Load to Calendar
                  </ActionMenuItemButton>
                  <ActionMenuItemButton
                    type='button'
                    onClick={() => { setProgramToShare(selectedProgram); closeActionMenu(); }}
                  >
                    <SharePlaneIcon size={16} color='#1A1A1A' />
                    Share
                  </ActionMenuItemButton>
                  <ActionMenuDivider />
                  <ActionMenuItemButton
                    type='button'
                    onClick={() => { setProgramToDelete(selectedProgram); closeActionMenu(); }}
                  >
                    <Icon icon='delete' size={16} color='#FF0505' />
                    Delete
                  </ActionMenuItemButton>
                </ActionMenuContainer>,
                document.body,
              )}
            </ActionMenuWrapper>
          </HeaderActions>
        </HeaderContainer>

        <NavigatorPreviewPanel
          type='program'
          label
          maxWidth='700px'
        />
      </>
    );
  } else if (programsError && !isLoading) {
    mainContent = (
      <EmptyState>
        <Text styledTextProps={{ fontSize: '16px', fontWeight: 700 }}>
          Error loading programs
        </Text>
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          {programsError}
        </Text>
      </EmptyState>
    );
  } else if (isInvalidProgramId) {
    mainContent = (
      <EmptyState>
        <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>
          Program could not be found
        </Text>
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          The program ID in the URL does not exist in this account.
        </Text>
      </EmptyState>
    );
  } else {
    mainContent = (
      <SelectEmptyState>
        <EmptyStateArrow color={accountPrimaryColor || 'transparent'} />
        <EmptyStateText>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A' }}>
            Select a Program
          </Text>
          <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
            Select a program to view and edit
          </Text>
        </EmptyStateText>
      </SelectEmptyState>
    );
  }

  const programModals = (
    <>
      <LoadToCalendarModal
        isOpen={calPickerOpen}
        onClose={() => setCalPickerOpen(false)}
        accountCode={accountCode}
        program={selectedProgram}
      />

      <EditProgramModal
        isOpen={!!programToEdit}
        program={programToEdit}
        accountCode={accountCode}
        onClose={() => { setProgramToEdit(null); dispatch(fetchPrograms(accountCode)); }}
      />

      <ShareProgramModal
        isOpen={!!programToShare}
        program={programToShare}
        accountCode={accountCode}
        onClose={() => setProgramToShare(null)}
      />

      <ConfirmModal
        isOpen={!!programToDelete}
        onClose={() => setProgramToDelete(null)}
        onConfirm={confirmDeleteProgram}
        title='Delete Program'
        message={`Are you sure you want to delete "${programToDelete?.programName || 'this program'}"? This can't be undone.`}
        isLoading={isDeletingProgram}
      />
    </>
  );

  if (isMobile) {
    if (programId) {
      return (
        <MobilePage>
          <MobileHeader>
            <MobileBackLink
              type='button'
              onClick={() => history.push('/dashboard/programs')}
            >
              <Icon icon='left-arrow' size={12} color='#77A7FF' />
              Back to Programs
            </MobileBackLink>
          </MobileHeader>
          <MobileDetailContainer>
            {mainContent}
          </MobileDetailContainer>
          {programModals}
        </MobilePage>
      );
    }
    return (
      <MobilePage>
        <MobileHeader>
          <MobileBackLink
            type='button'
            onClick={() => history.push('/dashboard')}
          >
            <Icon icon='left-arrow' size={12} color='#77A7FF' />
            Back to Home
          </MobileBackLink>
          <MobileSectionTitle>Programs</MobileSectionTitle>
        </MobileHeader>
        <NavigatorSelectorPanel
          fullWidth
          buttons={selectorButtons}
          searchPlaceholder='Search programs'
          isLoading={isLoading}
          selectedId={selectedProgram?.id}
          restoreSelectedScrollRef={false}
          renderList={renderProgramList}
        />
        <AddImportProgramModal
          isOpen={isAddOpen}
          onClose={() => setIsAddOpen(false)}
          options={addProgramOptions}
        />
        <ProgramTemplatesModal
          isOpen={isTemplatesOpen}
          onClose={() => setIsTemplatesOpen(false)}
          onBack={() => { setIsTemplatesOpen(false); setIsAddOpen(true); }}
          accountCode={accountCode}
        />
      </MobilePage>
    );
  }

  return (
    <Container>
      <NavigatorSelectorPanel
        width={285}
        title='Programs'
        buttons={selectorButtons}
        searchPlaceholder='Search programs'
        isLoading={isLoading}
        selectedId={selectedProgram?.id}
        restoreSelectedScrollRef={Boolean(programId)}
        renderList={renderProgramList}
      />
      <Section>
        {mainContent}
      </Section>
      <ConfirmModal
        isOpen={pendingDeleteId !== null}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title='Delete unfinished import?'
        message="This will permanently remove the unfinished import. You'll need to upload your spreadsheet again to retry."
        isLoading={isAbandoning}
      />
      <AddImportProgramModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        options={addProgramOptions}
      />
      <ProgramTemplatesModal
        isOpen={isTemplatesOpen}
        onClose={() => setIsTemplatesOpen(false)}
        onBack={() => { setIsTemplatesOpen(false); setIsAddOpen(true); }}
        accountCode={accountCode}
      />
      {programModals}
    </Container>
  );
};

ProgramNavigator.propTypes = {
  accountCode: PropTypes.number,
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isMobile: PropTypes.bool,
};

ProgramNavigator.defaultProps = {
  accountCode: undefined,
  programId: undefined,
  isMobile: false,
};

export default ProgramNavigator;
